import type { ReactNode } from "react";
import { KeyRound, MessageSquare, ShieldCheck, Unplug } from "lucide-react";
import { DotChip } from "@/components/shared/DotChip";
import { makeTr, type LandingLocale } from "@/lib/landing/locale";
import { Container, MockFrame, Section, SectionHeading } from "./primitives";
import { StatusChip } from "./mock";

/** The connector address; the MCP server is published on the app's own domain (vercel.json). */
const MCP_URL = "https://zettlapp.com/mcp";

function Bubble({ from, children }: { from: "user" | "assistant"; children: ReactNode }) {
  return from === "user" ? (
    <div className="ml-auto max-w-[85%] rounded-2xl rounded-br-md bg-primary/15 px-4 py-2.5 text-[14px] leading-snug">
      {children}
    </div>
  ) : (
    <div className="max-w-[94%] text-[14px] leading-relaxed">{children}</div>
  );
}

/** Which Zettl tool the assistant called: the MCP tool name, as the assistant shows it. */
function ToolCall({ name, write }: { name: string; write?: boolean }) {
  return (
    <p className="mb-2 inline-flex items-center gap-2 rounded-md border bg-background/60 px-2 py-1 font-mono text-[11px] text-muted-foreground">
      <span
        className="size-1.5 rounded-full"
        style={{ backgroundColor: write ? "hsl(var(--dot-blue))" : "hsl(var(--dot-green))" }}
      />
      zettl · {name}
    </p>
  );
}

/**
 * The MCP connector: ask an AI assistant about your payments and documents.
 * Every line is backed by docs/Functions/MCP_CONNECTOR.md and
 * supabase/functions/mcp/tools.ts (the chat uses list_missing_documents and
 * match_receipt_to_transaction). Supplier names and amounts are invented.
 */
export function AiAssistantSection({ locale }: { locale: LandingLocale }) {
  const tr = makeTr(locale);

  const missing = [
    { date: "4. 9.", text: tr("Tiskárna Pixel", "Tiskárna Pixel"), amount: "−2 890,00 Kč" },
    { date: "17. 9.", text: tr("Parkování Centrum", "Parkování Centrum"), amount: "−120,00 Kč" },
    { date: "26. 9.", text: tr("Nájem kanceláře", "Office rent"), amount: "−18 500,00 Kč" },
  ];

  const facts: { icon: ReactNode; title: string; text: ReactNode }[] = [
    {
      icon: <KeyRound className="size-4" />,
      title: tr("Přihlášení účtem Zettl", "Sign in with Zettl"),
      text: tr("OAuth 2.1 a potvrzení na souhlasové obrazovce.", "OAuth 2.1 and a consent screen."),
    },
    {
      icon: <ShieldCheck className="size-4" />,
      title: tr("Jen čtení, nebo i změny", "Read only, or also changes"),
      text: tr(
        "Nebo navíc párování, kategorie a stav faktur.",
        "Or also matching, categories and invoice status."
      ),
    },
    {
      icon: <Unplug className="size-4" />,
      title: tr("Odpojení kdykoli", "Disconnect any time"),
      text: tr(
        "V Nastavení Zettlu, s okamžitou platností.",
        "In Zettl's settings, effective immediately."
      ),
    },
  ];

  return (
    <Section id="ai-asistent" labelledBy="ai-title" contours="side">
      <Container className="grid items-center gap-12 lg:grid-cols-[1fr_1.15fr] lg:gap-16">
        <div>
          <SectionHeading
            id="ai-title"
            eyebrow={tr("AI asistent · tarif Firma", "AI assistant · Firma plan")}
            title={tr(
              "Zeptejte se AI asistenta na své platby",
              "Ask an AI assistant about your payments"
            )}
            lead={tr(
              "Přes MCP server Zettlu, v AI asistentech s podporou vzdálených MCP konektorů, např. Claude.",
              "Through Zettl's MCP server, in AI assistants that support remote MCP connectors, such as Claude."
            )}
          />
          <p className="mt-6 inline-flex max-w-full items-center gap-2 rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 font-mono text-sm text-[hsl(var(--action))] dark:text-primary">
            <span className="size-1.5 shrink-0 rounded-full bg-[hsl(var(--action))] dark:bg-primary" />
            <span className="break-all">{MCP_URL}</span>
          </p>
          <ul className="mt-8 space-y-5">
            {facts.map((f) => (
              <li key={f.title} className="flex gap-3.5">
                <span
                  aria-hidden="true"
                  className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-primary/25 bg-primary/10 text-[hsl(var(--action))] dark:text-primary"
                >
                  {f.icon}
                </span>
                <div>
                  <h3 className="text-sm font-semibold">{f.title}</h3>
                  <p className="mt-0.5 text-sm leading-relaxed text-muted-foreground">{f.text}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <MockFrame
            label={tr(
              "Ukázka rozhovoru s AI asistentem napojeným na Zettl: chybějící doklady za září a spárování účtenky. Vymyšlená data.",
              "Sample chat with an AI assistant connected to Zettl: missing documents for September and matching a receipt. Invented data."
            )}
            className="bg-card"
          >
            <div className="flex items-center justify-between border-b px-4 py-3">
              <span className="flex items-center gap-2 text-[13px] font-medium">
                <MessageSquare className="size-4 text-muted-foreground" />
                {tr("AI asistent", "AI assistant")}
              </span>
              <DotChip color="green">{tr("Zettl připojen", "Zettl connected")}</DotChip>
            </div>
            <div className="space-y-5 p-4 sm:p-5">
              <Bubble from="user">
                {tr(
                  "Které platby za září ještě nemají doklad?",
                  "Which September payments still have no document?"
                )}
              </Bubble>
              <Bubble from="assistant">
                <ToolCall name="list_missing_documents" />
                <p>
                  {tr(
                    "Za září chybí doklad u tří plateb:",
                    "Three September payments have no document:"
                  )}
                </p>
                <div className="mt-2.5 divide-y rounded-lg border bg-background/50 text-[13px]">
                  {missing.map((m) => (
                    <div
                      key={m.date}
                      className="grid grid-cols-[3rem_minmax(0,1fr)_auto] items-center gap-x-3 px-3 py-2"
                    >
                      <span className="tabular-nums text-muted-foreground">{m.date}</span>
                      <span className="break-words">{m.text}</span>
                      <span className="whitespace-nowrap font-mono tabular-nums">{m.amount}</span>
                    </div>
                  ))}
                </div>
              </Bubble>
              <Bubble from="user">
                {tr(
                  "Spáruj účtenku z papírnictví s platbou z 12. 10.",
                  "Match the stationery receipt with the payment from 12 Oct."
                )}
              </Bubble>
              <Bubble from="assistant">
                <ToolCall name="match_receipt_to_transaction" write />
                <p>
                  {tr(
                    "Hotovo. Platba −1 249,00 Kč z 12. 10. má teď doklad od Papírnictví Kotva.",
                    "Done. The −1 249,00 Kč payment from 12 Oct now has its receipt from Papírnictví Kotva."
                  )}
                </p>
                <StatusChip status="matched" locale={locale} className="mt-2 text-foreground" />
              </Bubble>
            </div>
          </MockFrame>
        </div>
      </Container>
    </Section>
  );
}
